import React, { useState, useRef, useEffect } from "react";
import {
  Box, Typography, Button, Paper, Grid, FormControl, InputLabel,
  Select, MenuItem, TextField, CircularProgress, Alert, ButtonBase
} from "@mui/material";
import api from "../../api";

/**
 * BoxeUnBoxeur
 * - Flux solo pour juger un boxeur
 * - Un seul bouton + pour pénalités (couleur selon 0/1/2)
 * - finishMatch retourne winnerName
 *
 * Modifs :
 * - suppression de la zone "Debug — Aperçu des élèves" dans les paramètres
 * - suppression de la fenêtre droite dans l'UI de match (doublon)
 * - ajout du label "Pénalités" à côté du bouton +1
 * - n'affiche les menus déroulants Élève / Arbitre QUE si une classe est sélectionnée.
 */

const sanctionLabels = {
  0: "",
  1: "AVERTISSEMENT",
  2: "MISE EN GARDE",
  3: "DISQUALIFICATION"
};

export default function BoxeUnBoxeur({ onFinish, onBack, classesList = [], getElevesFromClasse, initialClasseId, isStudentSession = false }) {
  const [mode, setMode] = useState("settings"); // settings | match
  const [nbReprises, setNbReprises] = useState(1);
  const [durationMin, setDurationMin] = useState(0);
  const [durationSec, setDurationSec] = useState(30);
  const [type, setType] = useState("poings");
  const [valeurPoing, setValeurPoing] = useState(1);
  const [valeurPied, setValeurPied] = useState(2);

  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classesError, setClassesError] = useState("");
  const [classeId, setClasseId] = useState(initialClasseId || "");

  const [eleves, setEleves] = useState([]);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [elevesFetchError, setElevesFetchError] = useState("");

  const [boxerId, setBoxerId] = useState("");
  const [arbitreId, setArbitreId] = useState("");
  const [nomBoxer, setNomBoxer] = useState("");

  const [settingsError, setSettingsError] = useState("");

  // Match state
  const [currentRound, setCurrentRound] = useState(1);
  const [time, setTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [rounds, setRounds] = useState([]);
  const timerRef = useRef();
  const audioRef = useRef();

  // Normalizers
  function normalizeClasses(raw = []) {
    return raw.map(c => {
      const id = String(c._id ?? c.id ?? c.classeId ?? c.uuid ?? "");
      const nom = c.nom ?? c.name ?? c.label ?? c.title ?? `Classe ${id}`;
      return { id, nom, raw: c };
    }).filter(x => x.id);
  }
  function normalizeEleves(raw = []) {
    return raw.map(e => {
      const id = String(e._id ?? e.id ?? e.id_eleve ?? e.uuid ?? "");
      const nom = `${e.prenom ? e.prenom + " " : ""}${e.nom ?? e.name ?? e.fullname ?? id}`;
      return { id, nom, raw: e };
    }).filter(x => x.id);
  }

  // Load classes
  async function loadClasses(force = false) {
    if (!force && Array.isArray(classesList) && classesList.length > 0) {
      setAvailableClasses(normalizeClasses(classesList));
      if (initialClasseId) {
        setClasseId(initialClasseId);
      }
      return;
    }
    setLoadingClasses(true);
    setClassesError("");
    try {
      const res = await api.get("/classes");
      const raw = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.data) ? res.data.data : []);
      setAvailableClasses(normalizeClasses(raw));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("BoxeUnBoxeur: GET /classes failed", err);
      setClassesError(err?.response?.data?.error || err?.message || "Impossible de charger les classes.");
      setAvailableClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }

  useEffect(() => { loadClasses(false); }, []); // eslint-disable-line

  // Try endpoints to fetch eleves
  async function tryFetchEleves(id) {
    const attempts = [
      { url: `/jugeboxe/eleves?classe=${encodeURIComponent(id)}`, hint: "jugeboxe/eleves?classe=" },
      { url: `/classes/${encodeURIComponent(id)}/eleves`, hint: "classes/:id/eleves" },
      { url: `/classes/${encodeURIComponent(id)}/students`, hint: "classes/:id/students" },
      { url: `/eleves?classe=${encodeURIComponent(id)}`, hint: "eleves?classe=" },
      { url: `/eleves?classeId=${encodeURIComponent(id)}`, hint: "eleves?classeId=" },
      { url: `/students?classe=${encodeURIComponent(id)}`, hint: "students?classe=" },
      { url: `/students?classId=${encodeURIComponent(id)}`, hint: "students?classId=" },
    ];

    for (const a of attempts) {
      try {
        // eslint-disable-next-line no-console
        console.log(`BoxeUnBoxeur: attempting ${a.hint} -> ${a.url}`);
        const res = await api.get(a.url);
        // eslint-disable-next-line no-console
        console.log(`BoxeUnBoxeur: response from ${a.hint}:`, res && res.data);
        if (Array.isArray(res.data)) return { list: res.data, used: a.hint, url: a.url };
        if (res.data && Array.isArray(res.data.items)) return { list: res.data.items, used: a.hint, url: a.url };
        if (res.data && Array.isArray(res.data.data)) return { list: res.data.data, used: a.hint, url: a.url };
        if (res.data && typeof res.data === "object") {
          const arrKey = Object.keys(res.data).find(k => Array.isArray(res.data[k]));
          if (arrKey) return { list: res.data[arrKey], used: a.hint, url: a.url };
        }
        return { list: [], used: a.hint, url: a.url };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`BoxeUnBoxeur: attempt ${a.hint} failed`, err?.response?.status || err?.message);
      }
    }
    throw new Error("Aucun endpoint valide trouvé pour récupérer les élèves (voir console pour tentatives).");
  }

  // fetch eleves
  async function fetchElevesForClasse(id) {
    setElevesFetchError("");
    if (!id) {
      setEleves([]);
      setBoxerId("");
      setArbitreId("");
      return;
    }
    setLoadingEleves(true);
    try {
      let list = [];
      if (getElevesFromClasse && typeof getElevesFromClasse === "function") {
        list = await getElevesFromClasse(id) || [];
      } else {
        const resp = await tryFetchEleves(id);
        list = resp.list ?? [];
      }

      if (!Array.isArray(list)) {
        console.warn("BoxeUnBoxeur: students response is not an array:", list);
        setEleves([]);
        setElevesFetchError("Format inattendu reçu pour la liste d'élèves (voir console).");
        return;
      }

      const normalized = normalizeEleves(list);
      setEleves(normalized);
      setElevesFetchError("");
      if (normalized.length > 0) {
        setBoxerId(prev => prev || normalized[0].id);
      } else {
        setBoxerId("");
      }
    } catch (err) {
      console.error("BoxeUnBoxeur: failed loading eleves", err);
      setEleves([]);
      setBoxerId("");
      setElevesFetchError(err?.message || "Impossible de charger les élèves.");
    } finally {
      setLoadingEleves(false);
    }
  }

  useEffect(() => {
    if (classeId) fetchElevesForClasse(classeId);
    else {
      setEleves([]);
      setBoxerId("");
      setArbitreId("");
      setElevesFetchError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classeId]);

  // helpers & match logic
  const initialTimeSec = () => (Number(durationMin || 0) * 60) + Number(durationSec || 0);
  function createEmptyRounds(count) {
    const c = Number(count) || 1;
    return Array(c).fill().map(() => ({
      scoreBoxer: { poings: 0, pieds: 0 },
      sanctionsBoxer: 0,
      endedBySanction: false
    }));
  }

  function startMatch() {
    setSettingsError("");
    if (isStudentSession && !classeId) {
      setSettingsError("La classe liée au QR élève n'a pas été détectée.");
      return;
    }
    if (classeId) {
      if (!boxerId) { setSettingsError("Sélectionnez un boxeur."); return; }
      if (arbitreId && (arbitreId === boxerId)) { setSettingsError("L'arbitre ne peut pas être le boxeur."); return; }
    } else {
      if (!nomBoxer) { setSettingsError("Renseignez le nom du boxeur en mode manuel."); return; }
    }
    const r = createEmptyRounds(nbReprises);
    setRounds(r);
    setCurrentRound(1);
    const t = initialTimeSec();
    setTime(t);
    setRunning(false);
    setMode("match");
  }

  function computeTotalPoints(rs) {
    return (rs || []).reduce((total, round) => {
      return total + (round?.scoreBoxer?.poings || 0) + (round?.scoreBoxer?.pieds || 0);
    }, 0);
  }

  function computeWinnerFromRounds(rs) {
    // For solo, winner is the boxeur unless disqualified; we can compute total points across rounds
    let total = 0;
    let disq = false;
    for (const round of rs) {
      if (round.sanctionsBoxer === 3) { disq = true; }
      total += (round.scoreBoxer.poings || 0) + (round.scoreBoxer.pieds || 0);
    }
    if (disq) return "Disqualifié";
    if (total > 0) return "Boxeur";
    return "—";
  }

  function finishMatch(forced = false) {
    // if forced is true, treat the boxeur as winner (unless disqualified)
    let winnerLabel = computeWinnerFromRounds(rounds);
    if (forced && winnerLabel !== "Disqualifié") winnerLabel = "Boxeur";
    let winnerName = "—";
    if (winnerLabel === "Boxeur") {
      winnerName = classeId ? (eleves.find(e => e.id === boxerId)?.nom || boxerId) : (nomBoxer || "Boxeur");
    } else if (winnerLabel === "Disqualifié") {
      winnerName = "Disqualifié";
    }
    const participants = {
      boxerId: classeId ? (boxerId || null) : null,
      arbitreId: classeId ? (arbitreId || null) : null,
      nomBoxer: !classeId ? nomBoxer || null : null,
    };
    const params = { nbReprises, durationMin, durationSec, type, valeurPoing, valeurPied, classeId: classeId || null, activity: "Boxe Française", nbBoxeurs: 1 };
    const soloBoxerPoints = computeTotalPoints(rounds);
    const result = {
      params,
      rounds,
      participants,
      winner: winnerLabel,
      winnerName,
      soloBoxerId: participants.boxerId,
      soloBoxerName: winnerLabel === "Boxeur" ? winnerName : (displayBoxer() || "Boxeur"),
      soloBoxerPoints,
      boxerPoints: soloBoxerPoints,
      createdAt: new Date().toISOString()
    };
    if (typeof onFinish === "function") onFinish(result);
    setMode("settings");
  }

  useEffect(() => {
    if (mode !== "match") return;
    if (running && time > 0) { timerRef.current = setInterval(() => setTime(t => t - 1), 1000); }
    if ((!running || time <= 0) && timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (time === 0 && running) {
      setRunning(false);
      if (audioRef.current) audioRef.current.play().catch(() => {});
      setTimeout(() => {
        if (currentRound < rounds.length) { setCurrentRound(c => c + 1); setTime(initialTimeSec()); setRunning(false); }
        else finishMatch();
      }, 800);
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, time, mode, currentRound, rounds]);

  function handleScore(typeName, value = 1) {
    const idx = currentRound - 1;
    setRounds(rs => rs.map((r, i) => {
      if (i !== idx) return r;
      const newScore = { ...(r.scoreBoxer || {}) };
      newScore[typeName] = Math.max(0, (newScore[typeName] || 0) + value);
      return { ...r, scoreBoxer: newScore };
    }));
  }

  function touchHandlers(action) {
    return {
      onClick: () => {
        action();
      }
    };
  }

  function handleSanction(delta) {
    const idx = currentRound - 1;
    setRounds(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      let sb = r.sanctionsBoxer || 0;
      sb = Math.max(0, Math.min(3, sb + delta));
      const newR = { ...r, sanctionsBoxer: sb };
      if (sb === 3) {
        newR.endedBySanction = true;
        setTimeout(() => { finishMatch(); }, 600);
      }
      return newR;
    }));
    setRunning(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }

  function resetCurrentRound() {
    const idx = currentRound - 1;
    setRounds(rs => rs.map((r, i) => i === idx ? { scoreBoxer: { poings: 0, pieds: 0 }, sanctionsBoxer: 0, endedBySanction: false } : r));
    setTime(initialTimeSec());
    setRunning(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }

  const displayBoxer = () => (classeId ? (eleves.find(e => e.id === boxerId)?.nom || boxerId) : (nomBoxer || "Boxeur"));
  const displayArbitre = () => (classeId ? (eleves.find(e => e.id === arbitreId)?.nom || arbitreId) : "—");

  function sanctionColorClass(count) {
    if (count >= 2) return "error";
    if (count === 1) return "warning";
    return "success";
  }

  const isThreeColumns = type === "piedspoings";
  const actionColumns = isThreeColumns ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))";
  const primaryButtonHeight = isThreeColumns ? { xs: 132, sm: 148 } : { xs: 156, sm: 172 };
  const primaryIconSize = isThreeColumns ? { xs: 42, sm: 48 } : { xs: 54, sm: 60 };
  const primaryLabelSize = isThreeColumns ? { xs: 14, sm: 16 } : { xs: 16, sm: 18 };
  const primaryScoreSize = isThreeColumns ? { xs: 26, sm: 30 } : { xs: 32, sm: 36 };
  const primaryDeltaSize = isThreeColumns ? { xs: 12, sm: 13 } : { xs: 13, sm: 15 };
  const penaltyTopSize = isThreeColumns ? { xs: 28, sm: 32 } : { xs: 34, sm: 38 };
  const secondaryButtonHeight = isThreeColumns ? 36 : 40;

  function renderScoreButton({ typeName, label, imageSrc, imageAlt, value, score, bgColor, borderColor, textColor }) {
    return (
      <ButtonBase
        {...touchHandlers(() => handleScore(typeName, value))}
        aria-label={`Ajouter ${label.toLowerCase()}`}
        sx={{
          width: "100%",
          minHeight: primaryButtonHeight,
          borderRadius: 3,
          bgcolor: bgColor,
          border: `2px solid ${borderColor}`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: isThreeColumns ? 0.45 : 0.6,
          py: isThreeColumns ? 1 : 1.2,
          px: isThreeColumns ? 0.5 : 0.75,
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <Box
          component="img"
          src={imageSrc}
          alt={imageAlt}
          sx={{
            width: primaryIconSize,
            height: primaryIconSize,
            objectFit: "contain"
          }}
        />
        <Typography sx={{ fontSize: primaryLabelSize, fontWeight: 900, lineHeight: 1.1 }}>{label}</Typography>
        <Typography sx={{ fontSize: primaryScoreSize, fontWeight: 900, lineHeight: 1 }}>{score}</Typography>
        <Typography sx={{ fontSize: primaryDeltaSize, color: textColor, lineHeight: 1.1 }}>+{value}</Typography>
      </ButtonBase>
    );
  }

  function renderPenaltyButton(count) {
    return (
      <Button
        {...touchHandlers(() => handleSanction(1))}
        variant="contained"
        color={sanctionColorClass(count)}
        sx={{
          width: "100%",
          minHeight: primaryButtonHeight,
          fontSize: primaryLabelSize,
          fontWeight: 900,
          borderRadius: 3,
          lineHeight: 1.15,
          display: "flex",
          flexDirection: "column",
          gap: isThreeColumns ? 0.45 : 0.6,
          touchAction: "manipulation",
          px: isThreeColumns ? 0.5 : 0.75,
          py: isThreeColumns ? 1 : 1.2,
        }}
      >
        <Box component="span" sx={{ fontSize: penaltyTopSize, lineHeight: 1 }}>+1</Box>
        <Box component="span">Pénalité</Box>
        <Box component="span" sx={{ fontSize: primaryScoreSize, lineHeight: 1 }}>{count}</Box>
        <Box component="span" sx={{ fontSize: primaryDeltaSize }}>{sanctionLabels[count] || "AUCUNE"}</Box>
      </Button>
    );
  }

  // Render settings
  if (mode === "settings") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fff", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Paper sx={{ p: 3, minWidth: 320, width: "95%", maxWidth: 900 }}>
          <Typography variant="h4" align="center" sx={{ mb: 2 }}>Paramètres du match — 1 boxeur</Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Nombre de reprises</Typography>
            <TextField type="number" value={nbReprises} onChange={e => setNbReprises(Math.max(1, Number(e.target.value)))} inputProps={{ min: 1, style: { width: 64, textAlign: "center" } }} sx={{ bgcolor: "#fff", borderRadius: 1, mt: 1 }} />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Durée d'une reprise</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <TextField type="number" value={durationMin} onChange={e => setDurationMin(Math.max(0, Number(e.target.value)))} inputProps={{ min: 0, style: { width: 60, textAlign: "center" } }} />
              </Grid>
              <Grid item><Typography>minutes</Typography></Grid>
              <Grid item>
                <TextField type="number" value={durationSec} onChange={e => setDurationSec(Math.max(0, Math.min(59, Number(e.target.value))))} inputProps={{ min: 0, max: 59, style: { width: 60, textAlign: "center" } }} />
              </Grid>
              <Grid item><Typography>secondes</Typography></Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Type de match</Typography>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select value={type} onChange={e => setType(e.target.value)} size="small">
                <MenuItem value="poings">Poings</MenuItem>
                <MenuItem value="piedspoings">Pieds/Poings</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {type === "piedspoings" && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Valeur des coups</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item><TextField label="Poing" type="number" value={valeurPoing} onChange={e => setValeurPoing(Math.max(0, Number(e.target.value)))} size="small" /></Grid>
                <Grid item><TextField label="Pied" type="number" value={valeurPied} onChange={e => setValeurPied(Math.max(0, Number(e.target.value)))} size="small" /></Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 1, mb: 2 }}>
            {!isStudentSession && <Typography variant="subtitle1">Sélection de la classe (optionnel)</Typography>}
            {loadingClasses ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}>
                <CircularProgress size={20} /><Typography>Chargement des classes...</Typography>
              </Box>
            ) : classesError ? (
              <Box sx={{ mb: 2 }}><Alert severity="error">{classesError}</Alert><Button onClick={() => loadClasses(true)} variant="outlined" size="small">Réessayer</Button></Box>
            ) : (
              <>
                {isStudentSession ? null : (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Classe</InputLabel>
                    <Select value={classeId} label="Classe" onChange={e => setClasseId(e.target.value)} size="small">
                      <MenuItem value="">Aucune</MenuItem>
                      {availableClasses.map(c => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}

                {/* Only show selects when a class is chosen — otherwise allow manual name input (consistent with 2-boxer page) */}
                {classeId ? (
                  loadingEleves ? <Typography>Chargement des élèves...</Typography> :
                    elevesFetchError ? <Alert severity="error">{elevesFetchError}</Alert> : (
                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Élève</InputLabel>
                            <Select value={boxerId} label="Élève" onChange={e => setBoxerId(e.target.value)} size="small">
                              <MenuItem value="">Aucun</MenuItem>
                              {eleves.map(el => <MenuItem key={el.id} value={el.id}>{el.nom}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          {/* Arbitre enlarged: fullWidth + minWidth to ensure label visibility */}
                          <FormControl fullWidth sx={{ minWidth: 220 }}>
                            <InputLabel>Arbitre (optionnel)</InputLabel>
                            <Select value={arbitreId} label="Arbitre (optionnel)" onChange={e => setArbitreId(e.target.value)} size="small">
                              <MenuItem value="">— Aucun —</MenuItem>
                              {eleves.map(el => <MenuItem key={el.id} value={el.id}>{el.nom}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    )
                ) : !isStudentSession ? (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <TextField label="Nom du boxeur" value={nomBoxer} onChange={e => setNomBoxer(e.target.value)} fullWidth />
                    </Grid>
                  </Grid>
                ) : null}
              </>
            )}
          </Box>

          {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button variant="outlined" color="secondary" onClick={() => { if (typeof onBack === "function") onBack(); }}>Retour</Button>
            <Button variant="contained" color="primary" onClick={startMatch}>Lancer le match</Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // === Match UI ===
  const round = rounds[currentRound - 1] || {};

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6fb", display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
      <audio src="/assets/bip.mp3" ref={audioRef} preload="auto" />
      <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 3 }, minWidth: 320, width: "95%", maxWidth: 900, borderRadius: 4 }}>
        <Box sx={{ textAlign: "center", mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Reprise {currentRound} / {rounds.length}
          </Typography>
          <Typography sx={{ fontSize: { xs: 42, sm: 56 }, fontWeight: 900, lineHeight: 1.1, mt: 1 }}>
            {`${Math.floor(time / 60)}:${(time % 60).toString().padStart(2, "0")}`}
          </Typography>
          <Button variant="contained" onClick={() => setRunning(r => !r)} sx={{ mt: 1.5, minWidth: 170, minHeight: 52, fontSize: 18, fontWeight: 700 }}>
            {running ? "Pause" : "Démarrer"}
          </Button>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "#555", fontWeight: 700 }}>
            Juge arbitre : {displayArbitre()}
          </Typography>
        </Box>

        <Paper sx={{ bgcolor: "#f5faff", p: { xs: 1.25, sm: 2 }, borderRadius: 4, mb: 2.5 }}>
          <Typography sx={{ textAlign: "center", fontSize: 22, fontWeight: 900, color: "#1565c0", mb: 1.5 }}>
            Boxeur — {displayBoxer()}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: actionColumns,
              gap: 1,
              width: "100%",
              alignItems: "stretch"
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {renderScoreButton({
                typeName: "poings",
                label: "Poings",
                imageSrc: "/assets/gantbleu.png",
                imageAlt: "Poing",
                value: valeurPoing,
                score: round?.scoreBoxer?.poings ?? 0,
                bgColor: "#dceeff",
                borderColor: "#1976d2",
                textColor: "#174a7c",
              })}
            </Box>

            {type === "piedspoings" && (
              <Box sx={{ minWidth: 0 }}>
                {renderScoreButton({
                  typeName: "pieds",
                  label: "Pieds",
                  imageSrc: "/assets/whiteshoes.png",
                  imageAlt: "Pied",
                  value: valeurPied,
                  score: round?.scoreBoxer?.pieds ?? 0,
                  bgColor: "#eef3ff",
                  borderColor: "#5a64ea",
                  textColor: "#3840b8",
                })}
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              {renderPenaltyButton(round?.sanctionsBoxer ?? 0)}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: actionColumns,
              gap: 1,
              width: "100%",
              mt: 0.75
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Button fullWidth variant="text" color="inherit" onClick={() => handleScore("poings", -valeurPoing)} sx={{ minHeight: secondaryButtonHeight, fontSize: isThreeColumns ? 12 : 14, fontWeight: 700 }}>
                - Poing
              </Button>
            </Box>
            {type === "piedspoings" && (
              <Box sx={{ minWidth: 0 }}>
                <Button fullWidth variant="text" color="inherit" onClick={() => handleScore("pieds", -valeurPied)} sx={{ minHeight: secondaryButtonHeight, fontSize: isThreeColumns ? 12 : 14, fontWeight: 700 }}>
                  - Pied
                </Button>
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Button fullWidth variant="text" color="inherit" onClick={() => handleSanction(-1)} sx={{ minHeight: secondaryButtonHeight, fontSize: isThreeColumns ? 12 : 14, fontWeight: 700 }}>
                - Pénalité
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          <Button variant="outlined" onClick={() => { resetCurrentRound(); }} sx={{ minHeight: 48, fontWeight: 700 }}>
            Remise à zéro
          </Button>
          <Grid container spacing={1.2}>
            <Grid item xs={6}>
              <Button fullWidth disabled={currentRound === 1} onClick={() => { setCurrentRound(c => c - 1); setTime(initialTimeSec()); setRunning(false); }} sx={{ minHeight: 48 }}>
                Reprise précédente
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button fullWidth disabled={currentRound === rounds.length} onClick={() => { setCurrentRound(c => c + 1); setTime(initialTimeSec()); setRunning(false); }} sx={{ minHeight: 48 }}>
                Reprise suivante
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button fullWidth variant="outlined" color="primary" onClick={() => finishMatch(true)} sx={{ minHeight: 52, fontWeight: 700 }}>
                Valider victoire
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button fullWidth variant="contained" color="success" onClick={() => finishMatch(false)} sx={{ minHeight: 52, fontWeight: 700 }}>
                Terminer et voir le résultat
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}